// web_components/wcBanner/wcCase.ts

import { WebComponent_Base } from '../WebComponent_Base';
import { WebComponent_Utils } from '../WebComponent_Utils';
import { renderCaseHTML } from './wcCase_Renderer';
import { handleFormSubmission } from './wcCase_Utils';
import { NotificationService, NotificationType } from '../../utils/NotificationService';

/**
 * A custom web component for creating a banner section with a full-width image and layered content.
 * The component supports responsive design and lazy loading for images.
 * Title, subtitle, paragraph, and CTA button overlay the image with a maximum width of 50% or 60 characters.
 */
export class WcCase extends WebComponent_Base {
	public dataAttributes: any;
	public instanceId: string;

	constructor() {
		super();
		this.dataAttributes = {};
		this.instanceId = WebComponent_Utils.generateUniqueId();
	}

	/**
	 * Gets the name of the component.
	 *
	 * This getter overrides the base class implementation to return the name
	 * of the component. This value is used for logging and
	 * identifying the component in error messages or other diagnostics.
	 *
	 * @returns {string} The name of the component`.
	 */
	protected get componentName(): string {
		return 'WcCase';
	}

	/**
	 * Retrieves data attributes.
	 * Called from the BaseComponent after stylesheets are attached.
	 */
	protected getComponentData(): void {
		this.getData();
	}

	/**
	 * Lifecycle hook called when the component is connected to the DOM.
	 */
	protected onConnected(): void {}

	/**
	 * Lifecycle hook called when the component is removed from the DOM.
	 */
	protected onDisconnected(): void {}

	/**
	 * Transfers data attributes from the light DOM elements to this.dataAttributes.
	 * This includes data-css for styling, store ids, product type, variables etc.
	 */
	private getData(): any {
		// Variables
		const variables = this.getAttribute('data-variables');
		if (variables) {
			try {
				this.dataAttributes['variables'] = JSON.parse(variables);
			} catch (e) {
				console.error('wcBanner Component: Invalid JSON in data-variables attribute:', e);
				this.dataAttributes['variables'] = null;
			}
		}

		// CSS
		const css = this.getAttribute('data-css');
		if (css) {
			try {
				this.dataAttributes['css'] = JSON.parse(css);
			} catch (e) {
				console.error('wcBanner Component: Invalid CSS in data-css attribute:', e);
				this.dataAttributes['css'] = null;
			}
		}
	}

	/**
	 * Renders the component based on the provided `dataAttributes` and `instanceId`.
	 * Appends the generated product content directly to the shadow DOM, after the attached styles.
	 *
	 * @protected
	 * @returns {HTMLElement | null} A reference to the first child of the appended content in the shadow DOM,
	 *                               or `null` if no content was appended.
	 */
	protected render(): HTMLElement | null {
		// Generate the content as a string
		const renderedHTML: string = renderCaseHTML(this.dataAttributes, this.instanceId);

		// Convert the string to a DOM node
		const template = document.createElement('template');
		template.innerHTML = renderedHTML;

		// Append the content from the template to the shadow DOM
		this.shadowRoot!.appendChild(template.content.cloneNode(true));

		// Return the reference to the first child of the appended content
		return this.shadowRoot!.lastElementChild as HTMLElement | null;
	}

	/**
	 * Adds event listeners.
	 */
	protected attachListeners(): void {
		// Trigger the case components slider buffer check function on load + window resize
		if (document.readyState === 'loading') {
			this.showSliderBufferAndButtons();
		} else {
			this.showSliderBufferAndButtons();
		}

		window.addEventListener('resize', () => {
			this.showSliderBufferAndButtons();

			// Update min-height of active contents after resize
			const contentsEl = this.shadowRoot?.querySelector<HTMLElement>('.wc-case_contents');
			if (!contentsEl || !contentsEl.classList.contains('active')) return;
			contentsEl.style.minHeight = `${contentsEl.scrollHeight}px`;
		});

		// Set first instance of wc-case as the active/default unless overridden in buildCaseOptions()
		document.querySelector('wc-case')?.setAttribute('data-active', 'true');

		// Detect a case code URL parameter to determine whether a specific case should be shown as default.
		const urlParams = new URLSearchParams(window.location.search);
		const caseSkuParam = urlParams.has('sku') ? urlParams.get('sku') : null;

		// Build case options for each case, passing in the selected case SKU if one was found above.
		this.buildCaseOptions(caseSkuParam as String);

		// Switch cases on radio button selection
		const radioButton = this.shadowRoot!.querySelectorAll('.wc-case__option-input');
		radioButton.forEach((el) => {
			el.addEventListener('click', () => this.switchCaseType(el));
		});

		// Handle 'Add to basket' form submission
		const form = this.shadowRoot!.querySelector('.wc-case__atb-form');
		const productIdInput = this.shadowRoot!.querySelector(
			'.wc-case__product-id',
		) as HTMLInputElement;
		const productTypeInput = this.shadowRoot!.querySelector(
			'.wc-case__product-type',
		) as HTMLInputElement;

		if (form) {
			form.addEventListener('submit', (event: Event) => {
				event.preventDefault(); // Prevent the default form submission behavior
				const productId =
					productIdInput instanceof HTMLInputElement
						? parseInt(productIdInput.value)
						: NaN;

				if (!Number.isInteger(productId)) {
					NotificationService.showNotification(
						NotificationType.error,
						'Add to Basket Failed',
						'Sorry, we could not add this item to your basket',
					);
					return;
				}

				// Retrieve the input values
				// Ensure that productType is a string by checking if productTypeInput is an input element
				const productType =
					productTypeInput instanceof HTMLInputElement
						? productTypeInput.value
						: 'default';

				// Custom form handling logic, like adding to cart or updating UI
				handleFormSubmission(this, productId, productType, '1', false);
			});
		}

		// Toggle the case contents if hidden by clicking the heading
		const caseContents = this.shadowRoot?.querySelector<HTMLElement>('.wc-case_contents');
		if (caseContents && caseContents.classList.contains('wc-case_contents-hidden')) {
			const caseContentsHeading = this.shadowRoot?.querySelector<HTMLElement>(
				'.wc-case_contents--heading',
			);
			if (!caseContentsHeading) return;
			caseContentsHeading.addEventListener(
				'click',
				() => {
					this.revealCaseContents(caseContents, caseContentsHeading);
				},
				{ once: true },
			);
		}
	}

	/**
	 * Reveals hidden case contents and updates the heading and track styles.
	 *
	 * @param {HTMLElement} caseContents - The container holding the case contents.
	 * @param {HTMLElement} caseContentsHeading - The clickable heading for the case contents.
	 */
	revealCaseContents(caseContents: HTMLElement, caseContentsHeading: HTMLElement): void {
		const heading = caseContentsHeading.querySelector<HTMLElement>('h3');
		const itemsTrack = caseContents.querySelector<HTMLElement>('.slider__track');
		if (!heading || !itemsTrack) return;

		const caseType = this.dataset.caseType;

		caseContents.classList.remove('wc-case_contents-hidden');
		heading.innerText = `Here's what's in your ${caseType ? caseType + ' ' : ''}Case`;

		setTimeout(() => {
			itemsTrack.style.removeProperty('opacity');
			requestAnimationFrame(() => {
				itemsTrack.classList.remove('slider__track--transition');
			});
		}, 10);
	}

	/**
	 * Coming.
	 * @param {HTMLElement} el - The case holding the 'data-active' attribute.
	 */
	checkDefaultCaseRadioOption(el: HTMLElement): void {
		// Set the default case's associated radio button to be checked
		const defaultCaseType = el.getAttribute('data-case-type') as String;
		const defaultCaseRadio = el.shadowRoot!.querySelector(
			`#wc-case-${defaultCaseType?.toLowerCase()}`,
		) as HTMLInputElement;
		if (defaultCaseRadio) defaultCaseRadio.checked = true;
	}

	/**
	 * Updates the displayed case type based on the selected radio button.
	 * Applies the `data-active` attribute to the matching <wc-case> element
	 * and synchronises radio input states.
	 *
	 * @param {Element} radioButton - The clicked radio button element.
	 */
	switchCaseType(radioButton: Element): void {
		const caseType = radioButton.getAttribute('data-case-type');
		if (!caseType) return;

		const allCases = document.querySelectorAll<HTMLElement>('wc-case');

		allCases.forEach((wcCase) => {
			const isActive = wcCase.getAttribute('data-case-type') === caseType;
			wcCase.setAttribute('data-active', String(isActive));

			if (isActive) {
				const inputList =
					wcCase.shadowRoot?.querySelectorAll<HTMLInputElement>('.wc-case__option-input');

				inputList?.forEach((input) => {
					const matches = input.getAttribute('data-case-type') === caseType;
					input.checked = matches;
					input.toggleAttribute('checked', matches);
				});
			}
		});
	}

	/**
	 * Displays the track buffer pseudo-element on the case contents slider
	 * if the total width of the bottles exceeds the visible container width.
	 */
	showSliderBufferAndButtons(): void {
		const shadowRoot = this?.shadowRoot;
		if (!shadowRoot) return;

		const caseContents = shadowRoot.querySelector<HTMLElement>('.wc-case_contents');
		const caseContentsSliderTrack = caseContents?.querySelector<HTMLElement>('.slider__track');
		const caseBottles = caseContents?.querySelectorAll('.wc-case__bottle');

		if (!caseContents || !caseContentsSliderTrack || !caseBottles?.length) return;

		const caseContentsWidth = caseContents.clientWidth;
		const totalBottlesWidth = 220 * caseBottles.length; // 200px bottle + 20px gap

		// '+ 4' is a slight buffer to account for rounding/pixel calculation discrepancies
		const shouldShowBufferAndButtons = totalBottlesWidth > caseContentsWidth + 4;
		caseContentsSliderTrack.classList.toggle('show-buffer-buttons', shouldShowBufferAndButtons);
	}

	/**
	 * Builds and initializes the case selection radio options inside the component.
	 *
	 * - Queries all `<wc-case>` elements on the page and creates a corresponding radio option
	 *   inside the component’s shadow DOM.
	 * - Removes the placeholder `<i>` tag once options are available.
	 * - If multiple cases exist, renders one radio input per case and sets the correct
	 *   default selection:
	 *   - If `caseSkuParam` matches a product code, that case becomes the active/checked one.
	 *   - Otherwise, the first `<wc-case>` element is set as active by default.
	 * - If only one case exists, the entire options wrapper is removed.
	 *
	 * @param {string} caseSkuParam - Optional SKU from the URL used to pre-select a specific case.
	 * @returns {void}
	 */
	buildCaseOptions(caseSkuParam: String): void {
		const shadowRoot = this?.shadowRoot;
		if (!shadowRoot) return;

		const cases = document.querySelectorAll<HTMLElement>('wc-case');
		const caseOptionsWrapper = shadowRoot.querySelector<HTMLElement>('.wc-case__options');
		if (!caseOptionsWrapper) return;

		// Remove empty <i> tag inside wrapper to remove the hidden attribute
		const emptyTag = caseOptionsWrapper.querySelector('i');
		if (!emptyTag) return;
		emptyTag.remove();

		if (cases.length > 1) {
			let firstCaseOption: HTMLElement | undefined;

			// Create a radio option for each case and append it
			cases.forEach((el: HTMLElement) => {
				const caseType = el.getAttribute('data-case-type');
				const caseCode = el.getAttribute('data-product-code');
				const caseTypeLowerCase = caseType?.toLowerCase();
				if (!caseType) return;

				const caseOptionHTML = `
				<label id="wc-case__option-${caseTypeLowerCase}" class="wc-case__option" for="wc-case-${caseTypeLowerCase}">
					<input type="radio" id="wc-case-${caseTypeLowerCase}" data-case-type="${caseType}" data-case-product-code="${caseCode}" value="${caseTypeLowerCase}" aria-labelledby="wc-case-${caseTypeLowerCase}-label" name="wc-case__option" class="wc-case__option-input" />
					<span id="wc-case-${caseTypeLowerCase}-label" class="wc-case__label">${caseType}</span>
				</label>
			`;

				const template = document.createElement('template');
				template.innerHTML = caseOptionHTML.trim();
				const newCaseOptionElement = template.content.firstElementChild;

				if (newCaseOptionElement instanceof HTMLElement) {
					caseOptionsWrapper.appendChild(newCaseOptionElement);
					if (!firstCaseOption) firstCaseOption = newCaseOptionElement;
				}

				// Initially set the first case option as the default/data-active one + input selected.
				const firstCase = document.querySelector('wc-case') as HTMLElement;
				if (!firstCase) return;

				if (caseSkuParam) {
					// If a URL SKU parameter is present, override the current default/data-active case + input selected.
					const caseMatchingSku = document.querySelector(
						`wc-case[data-product-code="${caseSkuParam}"]`,
					) as HTMLElement;
					if (!caseMatchingSku) return;
					const caseInputMatchingSku = caseMatchingSku.shadowRoot?.querySelector(
						`input[data-case-product-code="${caseSkuParam}"]`,
					) as HTMLInputElement;
					if (!caseInputMatchingSku) return;
					firstCase?.removeAttribute('data-active');
					caseMatchingSku.setAttribute('data-active', 'true');
					caseInputMatchingSku.checked = true;
				} else {
					// Otherwise, set the first instance of wc-case as the default/data-active case and check its matching product type checkbox
					const allCases = document.querySelectorAll<HTMLElement>('wc-case');
					const firstCaseOptionInput = firstCase.shadowRoot?.querySelector(
						'input',
					) as HTMLInputElement;
					if (!firstCaseOptionInput) return;
					allCases?.forEach((el) => {
						const input = el.shadowRoot?.querySelector(
							'input[name="wc-case__option"]',
						) as HTMLInputElement;
						if (input && !input.checked) return;
						firstCase?.setAttribute('data-active', 'true');
						firstCaseOptionInput.checked = true;
					});
				}
			});
		} else {
			// Only one case — remove the entire options wrapper
			shadowRoot.querySelector('.wc-case__options-wrapper')?.remove();
		}
	}

	/**
	 * Applies visibility settings to all elements within the shadow DOM based content.
	 * This ensures elements are shown or hidden according to the presence of variable data
	 * and the component settings file.
	 */
	protected conditionalVisibility(): void {
		WebComponent_Utils.applyVisibilitySettings(<ShadowRoot>this.shadowRoot);
	}

	/**
	 * Specifies which attributes this component observes for changes.
	 * This enables the `attributeChangedCallback` to respond when the `data-active` attribute is added or removed.
	 *
	 * @returns {string[]} An array of attribute names to observe.
	 */
	static get observedAttributes(): string[] {
		return ['data-active'];
	}

	/**
	 * Called automatically when the `data-active` attribute changes.
	 * Toggles visibility of the case and its contents using `opacity` and `visibility`,
	 * relying on CSS media queries to control layout via `display`.
	 *
	 * This avoids layout shifts and works consistently across browsers.
	 *
	 * @param {string} name - The name of the changed attribute.
	 * @param {string | null} oldValue - The previous value of the attribute.
	 * @param {string | null} newValue - The new value of the attribute.
	 */
	/**
	 * Called automatically when the `data-active` attribute changes.
	 * Adds or removes the `.active` class and applies `min-height` dynamically
	 * to prevent layout jumps when case contents appear.
	 *
	 * @param {string} name - The name of the changed attribute.
	 * @param {string | null} oldValue - The previous value of the attribute.
	 * @param {string | null} newValue - The new value of the attribute.
	 */
	attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null): void {
		if (name !== 'data-active') return;
		const isActive = newValue === 'true';
		const caseEl = this.shadowRoot?.querySelector<HTMLElement>('.wc-case');
		const contentsEl = this.shadowRoot?.querySelector<HTMLElement>('.wc-case_contents');

		if (!caseEl || !contentsEl) return;

		if (isActive) {
			// Apply measured min-height before making visible
			contentsEl.style.minHeight = contentsEl.scrollHeight + 'px';
			caseEl.classList.add('active');
			contentsEl.classList.add('active');
		} else {
			caseEl.classList.remove('active');
			contentsEl.classList.remove('active');
			contentsEl.style.minHeight = '0';
		}
	}
}

/**
 * Registers the custom element:
 * - Checks if the component has already been defined to avoid registration errors
 * - Registers the component with the browser’s CustomElementRegistry
 * - Automatically re-registers the component if the page is restored from Safari’s back-forward cache (bfcache)
 */
WebComponent_Utils.defineComponent('wc-case', WcCase);
